import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  LinearProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  History as HistoryIcon,
  Undo as UndoIcon,
  GetApp as DownloadIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';

const ReconciliationPage = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const { apiCall } = useApi();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState(null);
  const [reconciliationHistory, setReconciliationHistory] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [reverseDialog, setReverseDialog] = useState(null);
  
  const locationName = decodeURIComponent(locationId);
  
  useEffect(() => {
    loadReconciliationHistory();
  }, [locationId]);
  
  const loadReconciliationHistory = async () => {
    try {
      setLoading(true);
      const response = await apiCall(
        `/api/v1/businesses/${encodeURIComponent(locationId)}/reconciliation/history`,
        'GET'
      );
      setReconciliationHistory(response.history || []);
    } catch (err) {
      console.error('Error loading reconciliation history:', err);
      setError('Failed to load reconciliation history');
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];
      
      if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls)$/i)) {
        setError('Please select a valid CSV or Excel file');
        return;
      }
      
      setSelectedFile(file);
      setError('');
    }
  };
  
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }
    
    try {
      setUploading(true);
      setError('');
      setSuccess('');
      
      // Read file content
      const fileContent = await readFileAsBase64(selectedFile);
      const fileType = selectedFile.name.toLowerCase().endsWith('.csv') ? 'csv' : 'xlsx';
      
      // Upload and process
      const response = await apiCall(
        `/api/v1/businesses/${encodeURIComponent(locationId)}/reconciliation/upload`,
        'POST',
        {
          filename: selectedFile.name,
          fileContent: fileContent,
          fileType: fileType
        }
      );

      // Handle the response format from backend
      if (response && response.results) {
        setUploadData({
          ...response,
          summary: {
            total_uploaded: response.results.total,
            matched: response.results.matched,
            unmatched: response.results.unmatched,
            match_percentage: response.results.matchPercentage
          }
        });
        setSuccess(`Processed ${response.results.total} transactions - ${response.results.matched} matched, ${response.results.unmatched} unmatched`);
        setActiveTab(1); // Switch to results tab
      } else {
        setError('Invalid response from server');
      }
      
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to process file');
    } finally {
      setUploading(false);
    }
  };
  
  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };
  
  const handleConfirmReconciliation = async () => {
    if (!uploadData || !uploadData.matched || uploadData.matched.length === 0) {
      setError('No matched transactions to reconcile');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await apiCall(
        `/api/v1/businesses/${encodeURIComponent(locationId)}/reconciliation/reverse`,
        'POST',
        {
          uploadId: uploadData.uploadId,
          matched: uploadData.matched,
          uploadData: uploadData.uploadData,
          summary: uploadData.summary,
          filename: selectedFile?.name
        }
      );
      
      setSuccess(response.message || 'Reconciliation completed successfully');
      setConfirmDialog(false);
      setUploadData(null);
      setSelectedFile(null);
      
      // Reload history
      await loadReconciliationHistory();
      setActiveTab(2); // Switch to history tab
      
    } catch (err) {
      console.error('Confirmation error:', err);
      setError(err.message || 'Failed to confirm reconciliation');
    } finally {
      setLoading(false);
    }
  };
  
  const handleReverseReconciliation = async (reconciliationId) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiCall(
        `/api/v1/reconciliation/${reconciliationId}/reverse`,
        'POST',
        {}
      );
      
      setSuccess(response.message || 'Reconciliation reversed successfully');
      setReverseDialog(null);
      
      // Reload history
      await loadReconciliationHistory();
      
    } catch (err) {
      console.error('Reverse error:', err);
      setError(err.message || 'Failed to reverse reconciliation');
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            <AccountBalanceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Transaction Reconciliation
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Location: {locationName}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => navigate('/locations')}
        >
          Back to Locations
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      <Card>
        <CardContent>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label="Upload File" icon={<UploadIcon />} />
            <Tab 
              label={`Results ${uploadData && uploadData.summary ? `(${uploadData.summary.total_uploaded})` : ''}`} 
              icon={<CheckIcon />} 
              disabled={!uploadData}
            />
            <Tab label="History" icon={<HistoryIcon />} />
          </Tabs>
          
          <Box sx={{ mt: 3 }}>
            {activeTab === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Upload POS Transaction File
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Upload your Point of Sale transaction file (CSV or Excel format) to reconcile with settled transactions.
                </Typography>
                
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    border: '2px dashed',
                    borderColor: selectedFile ? 'success.main' : 'grey.300',
                    backgroundColor: selectedFile ? 'success.light' : 'grey.50',
                    cursor: 'pointer'
                  }}
                  onClick={() => document.getElementById('file-input').click()}
                >
                  <input
                    id="file-input"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    hidden
                    onChange={handleFileSelect}
                  />
                  <UploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 2 }} />
                  <Typography variant="h6">
                    {selectedFile ? selectedFile.name : 'Click to select file'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Supported formats: CSV, Excel (.xlsx, .xls)
                  </Typography>
                </Paper>
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<UploadIcon />}
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                  >
                    {uploading ? 'Processing...' : 'Process File'}
                  </Button>
                </Box>
              </Box>
            )}
            
            {activeTab === 1 && uploadData && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Reconciliation Results
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'success.light' }}>
                      <CheckIcon sx={{ color: 'success.main', fontSize: 32 }} />
                      <Typography variant="h4">{uploadData?.summary?.matched || 0}</Typography>
                      <Typography variant="body2">Matched</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'error.light' }}>
                      <CancelIcon sx={{ color: 'error.main', fontSize: 32 }} />
                      <Typography variant="h4">{uploadData?.summary?.unmatched || 0}</Typography>
                      <Typography variant="body2">Unmatched</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'warning.light' }}>
                      <WarningIcon sx={{ color: 'warning.main', fontSize: 32 }} />
                      <Typography variant="h4">{uploadData?.summary?.total_already_reconciled || 0}</Typography>
                      <Typography variant="body2">Already Reconciled</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4">{uploadData?.summary?.total_uploaded || 0}</Typography>
                      <Typography variant="body2">Total Uploaded</Typography>
                    </Paper>
                  </Grid>
                </Grid>
                
                {uploadData.unmatched && uploadData.unmatched.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" color="error" gutterBottom>
                      Unmatched Transactions ({uploadData.unmatched.length})
                    </Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Date/Time</TableCell>
                            <TableCell>Terminal</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {uploadData.unmatched.map((tx, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{tx.datetime || `${tx.date} ${tx.time}`}</TableCell>
                              <TableCell>{tx.terminal || '-'}</TableCell>
                              <TableCell align="right">{formatCurrency(tx.amount)}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={tx.reason} 
                                  size="small" 
                                  color="error" 
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
                
                {uploadData.matched && uploadData.matched.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" color="success.main" gutterBottom>
                      Matched Transactions ({uploadData.matched.length})
                    </Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Date/Time</TableCell>
                            <TableCell>Terminal</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {uploadData.matched.slice(0, 10).map((match, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{match.uploaded.datetime}</TableCell>
                              <TableCell>{match.matched.terminal_id || match.matched.terminal}</TableCell>
                              <TableCell align="right">{formatCurrency(match.uploaded.amount)}</TableCell>
                              <TableCell>
                                <Chip 
                                  label="Matched" 
                                  size="small" 
                                  color="success" 
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                          {uploadData.matched.length > 10 && (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                <Typography variant="body2" color="text.secondary">
                                  ... and {uploadData.matched.length - 10} more
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={() => setConfirmDialog(true)}
                    disabled={!uploadData.matched || uploadData.matched.length === 0}
                    startIcon={<CheckIcon />}
                  >
                    Confirm Reconciliation
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      setUploadData(null);
                      setSelectedFile(null);
                      setActiveTab(0);
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
            
            {activeTab === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Reconciliation History
                </Typography>
                
                {loading ? (
                  <CircularProgress />
                ) : reconciliationHistory.length === 0 ? (
                  <Typography variant="body1" color="text.secondary">
                    No reconciliation history found
                  </Typography>
                ) : (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>File</TableCell>
                          <TableCell>User</TableCell>
                          <TableCell align="center">Matched</TableCell>
                          <TableCell align="center">Unmatched</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reconciliationHistory.map((record) => (
                          <TableRow key={record.id}>
                            <TableCell>{formatDate(record.created_at)}</TableCell>
                            <TableCell>{record.upload_filename}</TableCell>
                            <TableCell>{record.created_by}</TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={record.total_matched} 
                                size="small" 
                                color="success"
                              />
                            </TableCell>
                            <TableCell align="center">
                              {record.total_unmatched > 0 ? (
                                <Chip 
                                  label={record.total_unmatched} 
                                  size="small" 
                                  color="error"
                                />
                              ) : (
                                <Chip 
                                  label="0" 
                                  size="small" 
                                  variant="outlined"
                                />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={record.status === 'reversed' ? 'Reversed' : 'Active'}
                                size="small"
                                color={record.status === 'reversed' ? 'default' : 'primary'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {record.status === 'active' && 
                               (record.created_by === user?.userId || user?.role === 'admin') && (
                                <Tooltip title="Reverse Reconciliation">
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => setReverseDialog(record.id)}
                                  >
                                    <UndoIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
      
      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Confirm Reconciliation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reconcile {uploadData?.summary?.total_matched || 0} transactions?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action will mark these transactions as reconciled and they cannot be reconciled again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleConfirmReconciliation} 
            variant="contained" 
            color="success"
            disabled={loading}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Reverse Dialog */}
      <Dialog open={!!reverseDialog} onClose={() => setReverseDialog(null)}>
        <DialogTitle>Reverse Reconciliation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reverse this reconciliation?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will unmark all transactions from this reconciliation batch, allowing them to be reconciled again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReverseDialog(null)}>Cancel</Button>
          <Button 
            onClick={() => handleReverseReconciliation(reverseDialog)} 
            variant="contained" 
            color="warning"
            disabled={loading}
          >
            Reverse
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReconciliationPage;